import BarrelType from "../model/BarrelType";
import { collectComponents } from "./component";
import Screen from "../model/Screen";
import Component from "../model/Component";
import ApiLayer from "../api/model/snapshots/layers/Layer";
import ComponentSection from "../model/ComponentSection";
import Snapshot from "../api/model/snapshots/Snapshot";
import BasicRecord from "./BasicRecord";
import Barrel from "../model/Barrel";
import StyleguideData from "../model/StyleguideData";
import Layer from "../model/layer";
import InspectLayer from "../model/InspectLayer";
import ScreenVersion from "../model/ScreenVersion";
import { RemPreferences } from "../api/model";
import { getDensityScaleRepresentation } from "../utils/legacy";
import { PLATFORM } from "../enums";

function serializeData<T>(data: T, model: string) {
    return {
        model,
        data
    };
}

function serializeScreen(screenData: Screen) {
    const { name, description, tags } = screenData;

    return {
        model: "Screen",
        data: {
            name,
            description,
            tags
        }
    };
}

function serializeComponent(componentData: Component) {
    const {
        _id,
        name,
        description,
        latestVersion,
        variant
    } = componentData;

    const properties = variant && variant.values.map(({ propertyId: id, value }) => ({ id, value }));

    return {
        model: "Component",
        data: {
            id: _id,
            name,
            description,
            latestVersion,
            properties
        }
    };
}

function serializeVersion(versionData: ScreenVersion) {
    const data = Object.assign({}, versionData, { snapshot: Object.assign({}, versionData.snapshot) });
    const layers = data.snapshot.layers ?? [];

    data.snapshot.layers = layers.map(layerData => {
        const layer = new Layer(layerData as InspectLayer);

        return layer.toObject() as unknown as ApiLayer;
    });

    return {
        model: "Version",
        data
    };
}

function serializeVariantSection(variantSectionData: ComponentSection) {
    const propertyDescriptors = variantSectionData.variant!.properties
        .map(({ _id: id, name, values }) => ({ id, name, values }));

    const components = collectComponents([variantSectionData])
        .map(component => serializeComponent(component).data);

    return {
        model: "ComponentVariant",
        data: {
            name: variantSectionData.name,
            propertyDescriptors,
            components
        }
    };
}

function findLayerIndexPath(container: Snapshot | ApiLayer, layerId: string, idx: number[] = []): number[] | null {
    if (!container.layers?.length) {
        return null;
    }

    for (const [i, layer] of container.layers.entries()) {
        if (layer._id === layerId) {
            return idx.concat(i);
        }

        const rc = findLayerIndexPath(layer, layerId, idx.concat(i));

        if (rc) {
            return rc;
        }
    }

    return null;
}

type BarrelObject = ReturnType<InstanceType<typeof Barrel>["toObject"]>;
type PopulatedStyleguideObject = BarrelObject & { parent?: BarrelObject; };
type TransformedBarrelObject = BarrelObject & {
    density: string;
    remPreferences: RemPreferences & {
        rootFontSize?: number;
    };
    styleguide?: PopulatedStyleguideObject;
    parent?: PopulatedStyleguideObject;
};

/**
 * Populates linked styleguides of the styleguide given by `stid`
 *
 * Returns the styleguide with `stid`
 *
 * @param stid styleguide id
 * @param linkedStyleguides styleguide objects linked to styleguide with id `stid`
 * @param platform platform of the styleguide
 */
function populateStyleguides({
    stid,
    linkedStyleguides,
    platform
}: {
    stid: string;
    linkedStyleguides: BasicRecord<Barrel>;
    platform: PLATFORM;
}): PopulatedStyleguideObject {
    let itStyleguide = linkedStyleguides[stid];
    let itStyleguideObject: PopulatedStyleguideObject | null = itStyleguide.toObject();
    const firstStyleguideObject = itStyleguideObject;

    while (itStyleguide) {
        const nextStyleguide = linkedStyleguides[itStyleguide.parent!];

        if (nextStyleguide) {
            nextStyleguide.type = platform;
        }

        const parentObject = nextStyleguide?.toObject() ?? null;
        itStyleguide = nextStyleguide;
        itStyleguideObject!.parent = parentObject;
        itStyleguideObject = parentObject;
    }

    firstStyleguideObject.type = platform;

    return firstStyleguideObject;
}

/**
 * Serializes given barrel using `barrelType` and related styleguides
 */
function transformBarrelToObject({
    barrel,
    barrelType,
    styleguidesData,
    styleguides,
    platform
}: {
    barrel: Barrel;
    barrelType: BarrelType;
    styleguidesData: StyleguideData[];
    styleguides: BasicRecord<Barrel>;
    platform: PLATFORM;
}): TransformedBarrelObject {
    const barrelObject = barrel.toObject() as TransformedBarrelObject;

    barrelObject.remPreferences = {
        ...styleguidesData[0].remPreferences,
        rootFontSize: styleguidesData[0].remPreferences.remRootFontSize
    };

    if (barrelType === BarrelType.PROJECT && barrel.styleguideLink && barrel.styleguideLink.status === "active") {
        barrelObject.styleguide = populateStyleguides({
            stid: barrel.styleguideLink.styleguide._id,
            linkedStyleguides: styleguides,
            platform
        });
    } else if (barrelType === BarrelType.STYLEGUIDE && barrel.parent) {
        barrelObject.parent = populateStyleguides({
            stid: barrel.parent,
            linkedStyleguides: styleguides,
            platform
        });
    }

    return {
        ...barrelObject,
        type: platform,
        density: getDensityScaleRepresentation(platform, barrel.densityScale)
    };
}

export {
    serializeData,
    serializeScreen,
    serializeComponent,
    serializeVersion,
    serializeVariantSection,
    transformBarrelToObject,
    findLayerIndexPath,
    TransformedBarrelObject
};
